import { useEffect, useState } from "react";
import {
  getSyllabusTemplatesForSchedule,
} from "../../api/examsschedule/examApi";
import {
  getSyllabusTemplatesForSchedulePortal,
} from "../../api/examsschedule/examPortalApi";
import { getAllTemplates } from "../../api/syllabus/syllabusApi";
import { getAllTemplatesPortal } from "../../api/syllabus/syllabusPortalApi";
import {
  chapterOptionsFromTemplate,
  pickList,
  toId,
} from "../../utils/examsschedule/examHelpers";

/**
 * classId + subjectId (+ academicYear) → templates from syllabus module → chapters.
 * Syllabus is required for schedule create (enforced by parent form + backend).
 */
export default function SyllabusChapterSelect({
  isPortal = false,
  classId,
  subjectId,
  academicYear,
  syllabusTemplateId,
  chapterNos = [],
  onChange,
  disabled = false,
  required = true,
}) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    if (!classId || !subjectId) {
      setTemplates([]);
      setLoadError("");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setLoadError("");
        const filters = {
          classId,
          subjectId,
          academicYear: academicYear || undefined,
        };

        let list = [];
        try {
          const res = isPortal
            ? await getSyllabusTemplatesForSchedulePortal(filters)
            : await getSyllabusTemplatesForSchedule(filters);
          list = pickList(res, ["templates", "data"]);
        } catch (e) {
          list = [];
        }

        // Fallback: syllabus module list (same filters)
        if (list.length === 0) {
          try {
            const res2 = isPortal
              ? await getAllTemplatesPortal({
                  classId,
                  subjectId,
                  academicYear: academicYear || undefined,
                  isActive: true,
                })
              : await getAllTemplates({
                  classId,
                  subjectId,
                  academicYear: academicYear || undefined,
                  isActive: true,
                });
            list = pickList(res2, ["templates", "data"]);
          } catch (e2) {
            list = [];
          }
        }

        // If year filter yields nothing, retry without year
        if (list.length === 0 && academicYear) {
          try {
            const res3 = isPortal
              ? await getSyllabusTemplatesForSchedulePortal({
                  classId,
                  subjectId,
                })
              : await getSyllabusTemplatesForSchedule({ classId, subjectId });
            list = pickList(res3, ["templates", "data"]);
          } catch (e3) {
            /* ignore */
          }
          if (list.length === 0) {
            try {
              const res4 = isPortal
                ? await getAllTemplatesPortal({
                    classId,
                    subjectId,
                    isActive: true,
                  })
                : await getAllTemplates({
                    classId,
                    subjectId,
                    isActive: true,
                  });
              list = pickList(res4, ["templates", "data"]);
            } catch (e4) {
              /* ignore */
            }
          }
        }

        if (cancelled) return;
        setTemplates(list);
        if (list.length === 0) {
          setLoadError(
            "No active syllabus template for this class + subject. Create one under Syllabus Templates."
          );
        }
      } catch (e) {
        if (!cancelled) {
          setTemplates([]);
          setLoadError("Failed to load syllabus templates");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [classId, subjectId, academicYear, isPortal]);

  const selectedTemplate =
    templates.find((t) => toId(t) === toId(syllabusTemplateId)) || null;
  const chapterOpts = chapterOptionsFromTemplate(selectedTemplate);

  const toggleChapter = (no) => {
    const set = new Set(chapterNos || []);
    if (set.has(no)) set.delete(no);
    else set.add(no);
    onChange?.({
      syllabusTemplateId: toId(syllabusTemplateId) || null,
      chapterNos: [...set].sort((a, b) => a - b),
    });
  };

  const selectAllChapters = () => {
    if (!selectedTemplate) return;
    onChange?.({
      syllabusTemplateId: toId(syllabusTemplateId),
      chapterNos: chapterOpts.map((c) => c.value),
    });
  };

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-xs font-medium text-gray-500 dark:text-neutral-400 mb-1">
          Syllabus template {required ? "*" : ""}
        </label>
        <select
          disabled={disabled || loading || !classId || !subjectId}
          required={required}
          className="w-full border border-gray-300 dark:border-neutral-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-neutral-900"
          value={toId(syllabusTemplateId)}
          onChange={(e) =>
            onChange?.({
              syllabusTemplateId: e.target.value || null,
              chapterNos: [],
            })
          }
        >
          <option value="">
            {loading
              ? "Loading…"
              : !classId || !subjectId
              ? "Select class & subject first"
              : "Select syllabus template"}
          </option>
          {templates.map((t) => (
            <option key={toId(t)} value={toId(t)}>
              {t.title} ({t.academicYear})
              {t.chapters?.length ? ` · ${t.chapters.length} ch` : ""}
            </option>
          ))}
        </select>
        {loadError && (
          <p className="text-[11px] text-amber-700 mt-1">{loadError}</p>
        )}
      </div>

      {chapterOpts.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-xs font-medium text-gray-500 dark:text-neutral-400">
              Chapters {required ? "*" : ""}
            </label>
            <button
              type="button"
              disabled={disabled}
              onClick={selectAllChapters}
              className="text-[11px] text-indigo-600 hover:underline"
            >
              Select all
            </button>
          </div>
          <div className="max-h-40 overflow-y-auto border border-gray-200 dark:border-neutral-800 rounded-lg p-2 space-y-1">
            {chapterOpts.map((c) => (
              <label
                key={c.value}
                className="flex items-center gap-2 text-sm text-gray-700 dark:text-neutral-200 cursor-pointer"
              >
                <input
                  type="checkbox"
                  disabled={disabled}
                  checked={(chapterNos || []).includes(c.value)}
                  onChange={() => toggleChapter(c.value)}
                />
                {c.label}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
